import * as React from 'react'
  /* @jsx mdx */
import { mdx } from '@mdx-js/react';
/* @jsxRuntime classic */

/* @jsx mdx */

export const _frontmatter = {
  "title": "Science Wednesday: Self-driving cars",
  "description": "",
  "published": "2011-12-14T00:00:00.000Z",
  "redirect_from": ["/blog/science-wednesday-self-driving-cars/swizec/3178"],
  "categories": "Algorithm, Google, Robotics, San Francisco, Stanford, Uncategorized",
  "hero": "../../../defaultHero.jpg"
};
const layoutProps = {
  _frontmatter
};
const MDXLayout = "wrapper";
export default function MDXContent({
  components,
  ...props
}) {
  return <MDXLayout {...layoutProps} {...props} components={components} mdxType="MDXLayout">
    <p><img parentName="p" {...{
        "src": "https://swizec.com/blog/wp-content/uploads/2011/12/ai-car-lol7.jpg",
        "alt": "A DARPA challenge loser",
        "title": "A DARPA challenge loser"
      }}></img></p>
    <p><em parentName="p">{`Science Wednesday is a category I've wanted to create for a while. It will feature a weekly summary of an interesting academic article or other piece of science that I find - the primary reason being to force me into reading original sources and hopefully give you guys something interesting.`}</em></p>
    <p>{`Even though the world is a-buzz with news of a possible higgs boson discovery, I decided to start off Science Wednesday with excerpts from this week's lecture on robotics at ai-class.com. `}<a parentName="p" {...{
        "href": "http://en.wikipedia.org/wiki/Sebastian_Thrun",
        "title": "Sebastian Thrun"
      }}>{`Sebastian Thrun`}</a>{` talked about Stanford's autonomous vehicles and how the Stanford Racing Team managed to snag victory at the DARPA challenge a couple years ago.`}</p>
    <p>{`And when I say "snag victory", I mean `}<a parentName="p" {...{
        "href": "http://en.wikipedia.org/wiki/Stanley_%28vehicle%29"
      }}>{`Stanley`}</a>{` was the only car to even finish the race. The way they did this is really impressive and by now the technology has become so advanced the Google cars can drive in normal traffic on regular San Francisco roads!`}</p>
    <lite-youtube {...{
      "videoid": "V_BJUBpuvFE",
      "videostartat": "0"
    }}></lite-youtube>
    <lite-youtube {...{
      "videoid": "kqDvbguZsAA",
      "videostartat": "0"
    }}></lite-youtube>
    <p>{`That was the inspirational `}<em parentName="p">{`OMG THIS IS AWESOME`}</em>{` part of the lecture.`}</p>
    <p>{`Two algorithms are essential to achieving this: localization and `}<a parentName="p" {...{
        "href": "http://en.wikipedia.org/wiki/Motion_planning",
        "title": "Motion planning"
      }}>{`path planning`}</a>{`.`}</p>
    <h3 {...{
      "id": "localization"
    }}>{`Localization`}</h3>
    <p>{`For localization they use something called `}<a parentName="p" {...{
        "href": "http://en.wikipedia.org/wiki/Monte_Carlo_localization",
        "title": "Monte Carlo localization"
      }}>{`monte carlo localization`}</a>{`. It uses a particle filter as the beating heart of the approach - it sounds all fancy, but it's really quite a simple approach.`}</p>
    <p>{`Basically the idea is that you take measurements from your sensors and randomly scatter some points on your likely location. Then you compare measurements with whatever you know is correct for each point and assign appropriate weights - basically saying "Ok, if I am on a road marking there is a 90% chance the sensor will say 'White'".`}</p>
    <p>{`After some normalization you take points with the most weight and move them forward a bit, adding some scatter and eventually you become very certain of your location on the road because the algorithm converges into a single point.`}</p>
    <h3 {...{
      "id": "path-planning"
    }}>{`Path planning`}</h3>
    <p>{`For path planning two approaches are used. `}<a parentName="p" {...{
        "href": "http://en.wikipedia.org/wiki/Dynamic_programming",
        "title": "Dynamic programming"
      }}>{`Dynamic programming`}</a>{` is used to plan the path and a modified `}<a parentName="p" {...{
        "href": "http://en.wikipedia.org/wiki/A%2A_search_algorithm",
        "title": "A* search algorithm"
      }}>{`A`}{`*`}{` algorithm`}</a>{` is used to plan the actual movement within the map.`}</p>
    <p><img parentName="p" {...{
        "src": "https://swizec.com/blog/wp-content/uploads/2011/12/Weighted_A_star_with_eps_58.gif",
        "alt": "A* search that uses a heuristic that is 5.0(=ε...",
        "title": "A* search that uses a heuristic that is 5.0(=ε..."
      }}></img></p>
    <p>{`Dynamic programming enables the robot to find the shortest path from current state to goal. The approach is actually pretty simple - just backtracking from the goal to the start and subtracting the cost of movement, if you have stochastic actions (e.g. sometimes going forward makes you go left) you have to perform the whole calculation a few times until the algorithm converges.`}</p>
    <p>{`To plan the actual movement a modified A`}{`*`}{` algorithm is used. The biggest difference is that the algorithm works in a continous world instead of discrete and the cost estimation function considers limitations in the robot's movement.`}</p>
    <p>{`And here are two shiny videos that show off these two concepts.`}</p>
    <lite-youtube {...{
      "videoid": "xm9_vmIJeMk",
      "videostartat": "0"
    }}></lite-youtube>
    <iframe {...{
      "width": "100%",
      "height": 315,
      "src": "https://www.youtube-nocookie.com/embed/zS3st_7og3A?rel=0",
      "frameBorder": "0",
      "allow": "accelerometer; autoplay; encrypted-media; gyroscope; picture-in-picture",
      "allowFullScreen": true
    }}></iframe>
    <p>{`So there you have it, the basics for making a car that drives itself :)`}</p>
    <h6 {...{
      "id": "related-articles"
    }}>{`Related articles`}</h6>
    <ul>
      <li parentName="ul"><a parentName="li" {...{
          "href": "http://www.confused.com/news/motoring/car-insurance/self-drive-cars-tested-in-america-295336137"
        }}>{`Self-Drive Cars Tested In America`}</a>{` (confused.com)`}</li>
      <li parentName="ul"><a parentName="li" {...{
          "href": "http://spectrum.ieee.org/automaton/robotics/military-robots/heres-what-darpa-wants-to-see-from-their-crowdsourced-uav"
        }}>{`Here's What DARPA Wants to See From Their Crowdsourced UAV`}</a>{` (spectrum.ieee.org)`}</li>
      <li parentName="ul"><a parentName="li" {...{
          "href": "http://www.i-programmer.info/news/169-robotics/3395-inflatable-robots-safe-and-a-lot-of-fun.html"
        }}>{`Inflatable Robots - Safe and a Lot of Fun!`}</a>{` (i-programmer.info)`}</li>
      <li parentName="ul"><a parentName="li" {...{
          "href": "http://www.confused.com/news/motoring/car-insurance/google-develops-self-driving-car-3702181519"
        }}>{`Google develops self-driving car`}</a>{` (confused.com)`}</li>
      <li parentName="ul"><a parentName="li" {...{
          "href": "http://www.i-programmer.info/news/105-artificial-intelligence/3413-ai-at-the-crossroads-predicting-who-is-going-to-run-a-red-light.html"
        }}>{`AI at the Crossroads - predicting who is going to run a red light`}</a>{` (i-programmer.info)`}</li>
      <li parentName="ul"><a parentName="li" {...{
          "href": "http://io9.com/5842760/an-algorithm-that-makes-robots-appear-more-human"
        }}>{`An algorithm that makes robots appear more human `}{`[`}{`Video`}{`]`}</a>{` (io9.com)`}</li>
      <li parentName="ul"><a parentName="li" {...{
          "href": "http://www.fastcompany.com/1770045/this-week-in-bots-the-locomotion-edition"
        }}>{`This Week In Bots: The Locomotion Edition`}</a>{` (fastcompany.com)`}</li>
    </ul>
    <p><a parentName="p" {...{
        "href": "http://www.zemanta.com/",
        "title": "Enhanced by Zemanta"
      }}><img parentName="a" {...{
          "src": "https://img.zemanta.com/zemified_e.png?x-id=58646beb-aa86-49f1-a400-6111953c6304",
          "alt": "Enhanced by Zemanta"
        }}></img></a></p>

    </MDXLayout>;
}
;
MDXContent.isMDXComponent = true;
      